import React, { useMemo, useState } from 'react';
import { Platform, Text, TouchableOpacity, ActivityIndicator, View } from 'react-native';
import { useAccessibilityProps } from '../../../../shared/hooks/useAccessibilityProps';
import { ButtonProps } from '../../../../shared/components/ui/Button/ButtonProps';
import {
  buttonSizes,
  buttonVariants,
} from '../../../../shared/constants/global';
import Colors from '../../../../shared/theme/colors';
import { useScreenTheme } from '../../../../shared/hooks/useScreenTheme';
import { createButtonStyles } from '../../../../shared/components/ui/Button/Button.styles';

/**
 * Reusable Button component with support for:
 * - Variants (primary, secondary, etc.)
 * - Sizes (small, medium, large)
 * - Disabled and loading states
 * - Full width option
 */
const Button: React.FC<ButtonProps> = ({
  title,
  subTitle,
  customSubTitleStyle,
  customSubTitleTextStyle,
  onPress,
  variant = buttonVariants.primary,
  size = buttonSizes.large,
  disabled = false,
  loading = false,
  fullWidth = true,
  containerStyle,
  customTextStyle,
  showRightIcon = false,
  showLeftIcon = false,
  customLeftIcon,
  customRightIcon,
  customIconStyles,
  accessibilityLabel,
  accessibilityHint,
  accessibilityRole = 'button',
  accessibilityState,
  accessible = true,
  testID,
  focusable = true,
  customDisabledContainerStyle,
  customDisabledContainerText
}) => {
  const isDisabled = disabled || loading;
  const theme = useScreenTheme('button');
  const styles = useMemo(() => createButtonStyles(theme), [theme]);
  const a11yProps = useAccessibilityProps({
    accessibilityLabel: accessibilityLabel || title,
    accessibilityHint,
  });

  // Hooks must be called unconditionally (Rules of Hooks).
  // isHovered / isPressed are only used in the web branch but must be declared
  // at the top level so every render calls them in the same order.
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // ─── Web: TouchableOpacity with explicit keyboard handler ───────────────────
  // On web, React Native Web renders TouchableOpacity as a <div>. While it is
  // focusable and responds to mouse clicks, it does NOT natively respond to
  // Enter/Space keyboard events (those are button-specific browser behaviours).
  // We fix this by attaching an onKeyDown handler directly on the TouchableOpacity
  // so that screen-reader users can activate the button with the keyboard.
  if (Platform.OS === 'web') {
    const isSecondary = variant === 'secondary';
    const isPrimary = variant === 'primary';
    // Do not apply hover/pressed styles when the button is already in selected state
    // (e.g. an active tab) to prevent the hover background from overriding the
    // selected-tab highlight applied via containerStyle.
    const isSelected = accessibilityState?.selected === true;

    // Build the aria-label: append ", dimmed" when disabled so every AT
    // consistently announces the unavailable state, then append the hint.
    const effectiveLabel = accessibilityLabel || title;
    const disabledSuffix = isDisabled ? ', dimmed' : '';
    const hintSuffix = accessibilityHint ? `, ${accessibilityHint}` : '';
    const ariaLabel = `${effectiveLabel}${disabledSuffix}${hintSuffix}`;

    // Keyboard handler: activate on Enter or Space, matching native <button> behaviour.
    const handleWebKeyDown = (event: any) => {
      if (isDisabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        // Use optional chaining — RNTL synthetic events don't have preventDefault
        event.preventDefault?.();
        (onPress as any)(event);
      }
    };

    // Extract `selected` from accessibilityState so it can be passed as an
    // explicit aria-selected attribute on the DOM element. React Native Web
    // may omit aria-selected="false" when the value is false, causing screen
    // readers to report every tab as selected. Passing it explicitly ensures
    // the correct value is always present in the accessibility tree.
    const ariaSelected =
      accessibilityState && 'selected' in accessibilityState
        ? accessibilityState.selected
        : undefined;

    return (
      <TouchableOpacity
        activeOpacity={isDisabled ? 1 : 0.8}
        onPress={isDisabled ? undefined : (onPress as any)}
        // @ts-ignore — onKeyDown, onMouseEnter, onMouseLeave, onMouseDown, onMouseUp are web-only events
        onKeyDown={handleWebKeyDown}
        onMouseEnter={() => { if (!isDisabled) setIsHovered(true); }}
        onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
        onMouseDown={() => { if (!isDisabled) setIsPressed(true); }}
        onMouseUp={() => setIsPressed(false)}
        disabled={false}
        accessible={true}
        accessibilityRole={accessibilityRole}
        accessibilityLabel={ariaLabel}
        accessibilityState={{disabled: isDisabled, ...accessibilityState}}
        testID={testID}
        focusable={true}
        // @ts-ignore — tabIndex and aria-selected are web-only props
        tabIndex={0}
        // @ts-ignore
        aria-selected={ariaSelected}
        style={[
          styles.base,
          styles[size],
          fullWidth && styles.fullWidth,
          styles[variant],
          containerStyle,
          isSecondary && isHovered && !isDisabled && !isSelected && styles.secondaryHovered,
          isSecondary && isPressed && !isDisabled && !isSelected && styles.secondaryPressed,
          isPrimary && isHovered && !isDisabled && !isSelected && styles.primaryHovered,
          isPrimary && isPressed && !isDisabled && !isSelected && styles.primaryPressed,
          isDisabled && [styles.disabled, customDisabledContainerStyle],
        ]}
      >
        {loading ? (
          <ActivityIndicator
            color={variant === buttonVariants.primary ? Colors.white : Colors.black}
          />
        ) : (
          <View style={[styles.buttonTextContainer, customIconStyles]}>
            {showLeftIcon && (
              <View style={styles.iconWrapper}>
                {isDisabled && customLeftIcon
                  ? React.cloneElement(customLeftIcon as React.ReactElement<any>, {color: theme.ctaDisableText})
                  : customLeftIcon}
              </View>
            )}
            <Text
              style={[
                styles.textBase,
                styles.textTruncate,
                variant === buttonVariants.primary
                  ? styles.textPrimary
                  : styles.textSecondary,
                isDisabled && [styles.textDisabled, customDisabledContainerText],
                customTextStyle,
                
              ]}
              numberOfLines={1}
            >
              {title}
            </Text>
            {subTitle ? (
              <View style={[styles.subTitleBadge, customSubTitleStyle, isHovered && styles.subTitleHoveredBadge]}>
                <Text style={[styles.subTitleText, customSubTitleTextStyle]}>{subTitle}</Text>
              </View>
            ) : null}
            {showRightIcon && (
              <View style={styles.iconWrapper}>{customRightIcon}</View>
            )}
          </View>
        )}
      </TouchableOpacity>
    );
  }

  // ─── Native (iOS / Android): keep existing TouchableOpacity behaviour ────────
  const handlePress = (event: any) => {
    if (!isDisabled) {
      onPress(event);
    }
  };

  return (
    <TouchableOpacity
      activeOpacity={isDisabled ? 1 : 0.8}
      onPress={handlePress}
      disabled={isDisabled}
      accessible={accessible}
      accessibilityRole={accessibilityRole}
      {...a11yProps}
      accessibilityState={{ disabled: isDisabled, ...accessibilityState }}
      testID={testID}
      focusable={focusable !== undefined ? focusable : true}
      style={[
        styles.base,
        styles[size],
        fullWidth && styles.fullWidth,
        styles[variant],
        containerStyle,
        isDisabled && [styles.disabled, customDisabledContainerStyle],
      ]}
    >
      {loading ? (
        <ActivityIndicator
          color={
            variant === buttonVariants.primary ? Colors.white : Colors.black
          }
        />
      ) : (
        <View style={[styles.buttonTextContainer, customIconStyles]}>
          {showLeftIcon && (
            <View style={styles.iconWrapper}>
              {isDisabled && customLeftIcon
                ? React.cloneElement(customLeftIcon as React.ReactElement<any>, {color: theme.ctaDisableText})
                : customLeftIcon}
            </View>
          )}
          <Text
            style={[
              styles.textBase,
              styles.textTruncate,
              variant === buttonVariants.primary
                ? styles.textPrimary
                : styles.textSecondary,
              isDisabled && [styles.textDisabled, customDisabledContainerText],
              customTextStyle,
            ]}
            numberOfLines={1}
          >
            {title}
          </Text>
          {subTitle ? (
            <View style={[styles.subTitleBadge, customSubTitleStyle]}>
              <Text style={[styles.subTitleText, customSubTitleTextStyle]}>{subTitle}</Text>
            </View>
          ) : null}
          {showRightIcon && (
            <View style={styles.iconWrapper}>{customRightIcon}</View>
          )}
        </View>
      )}
    </TouchableOpacity>
  );
};

export default Button;
